import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'

import { CompensationReponse } from 'modules/attendance/components/compensation/payroll/response'

export interface CompensationResult {
  employee_id: number
  error?: string
}
export interface CustomResponseModalProps {
  response: CompensationReponse
  selected: AttendanceCustomPeriodEmployeeItemFragment[]
  onCloseModal: () => void
}

const CustomResponseModal = ({ response, selected, onCloseModal }: CustomResponseModalProps) => {
  const uniqueSuccessEmployeeIds = new Set(response.successes.map((success) => success.employee_id))
    .size

  const selectedById = new Map(selected.map((s) => [String(s.id), s]))

  const title = i18n.t(
    'time_tracking.modals.time_tracking_collection.bulk_compensation_response.title'
  )

  return (
    <Modal
      title={title}
      illustration="cash"
      footer={
        <Button onClick={onCloseModal}>
          {i18n.t('time_tracking.modals.time_tracking_collection.bulk_compensation_response.ok')}
        </Button>
      }
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gap="s20" width="full">
        <Text.Normal>
          {i18n.t(
            'time_tracking.modals.time_tracking_collection.bulk_compensation_response.summary',
            {
              created: uniqueSuccessEmployeeIds,
              total: new Set(selected.map((s) => s.id)).size,
            }
          )}
        </Text.Normal>

        {response.successes.length > 0 && (
          <Box justifyContent="flexStart" alignItems="flexStart" gap="s12" width="full">
            <Text.Normal weight="medium">
              {i18n.t(
                'time_tracking.modals.time_tracking_collection.bulk_compensation_response.create.created_for'
              )}
            </Text.Normal>
            <SuccessList successes={response.successes} selectedById={selectedById} />
          </Box>
        )}

        {response.errors.length > 0 && (
          <Box justifyContent="flexStart" alignItems="flexStart" gap="s12" width="full">
            <Text.Normal weight="medium">
              {i18n.t(
                'time_tracking.modals.time_tracking_collection.bulk_compensation_response.create.not_created_for'
              )}
            </Text.Normal>
            <CustomErrorList errors={response.errors} selectedById={selectedById} />
          </Box>
        )}
      </Box>
    </Modal>
  )
}

const CustomErrorList = ({
  errors,
  selectedById,
}: {
  errors: CompensationResult[]
  selectedById: Map<string, AttendanceCustomPeriodEmployeeItemFragment>
}) => {
  return (
    <Box
      maxHeight="s200"
      overflowY="scroll"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        all: 'abs016',
      }}
      width="full"
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gapDivider>
        {errors.map((error) => (
          <CustomErrorItem key={error.employee_id} error={error} selectedById={selectedById} />
        ))}
      </Box>
    </Box>
  )
}

const SuccessList = ({
  successes,
  selectedById,
}: {
  successes: CompensationResult[]
  selectedById: Map<string, AttendanceCustomPeriodEmployeeItemFragment>
}) => {
  const uniqueByEmployee = Array.from(new Map(successes.map((s) => [s.employee_id, s])).values())

  return (
    <Box
      maxHeight="s200"
      overflowY="scroll"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        all: 'abs016',
      }}
      width="full"
    >
      <Box justifyContent="flexStart" alignItems="flexStart" gapDivider>
        {uniqueByEmployee.map((success) => (
          <SuccessItem key={success.employee_id} success={success} selectedById={selectedById} />
        ))}
      </Box>
    </Box>
  )
}

const SuccessItem = ({
  success,
  selectedById,
}: {
  success: CompensationResult
  selectedById: Map<string, AttendanceCustomPeriodEmployeeItemFragment>
}) => {
  const employee = selectedById.get(String(success.employee_id))

  if (!employee) return <></>

  const employeeName = `${employee.firstName} ${employee.lastName}`

  return (
    <Box flexDirection="row" alignItems="center" gap="s16" padding="s16">
      <UserAvatar
        size="s40"
        user={{
          avatar: employee.access?.avatar,
          fullName: employeeName,
        }}
      />
      <Text.Normal weight="medium">{employeeName}</Text.Normal>
    </Box>
  )
}

const CustomErrorItem = ({
  error,
  selectedById,
}: {
  error: CompensationResult
  selectedById: Map<string, AttendanceCustomPeriodEmployeeItemFragment>
}) => {
  const employee = selectedById.get(String(error.employee_id))

  if (!employee) return <></>

  const employeeName = `${employee.firstName} ${employee.lastName}`

  return (
    <Box
      data-testid="employee-error"
      flexDirection="row"
      alignItems="center"
      gap="s16"
      padding="s16"
    >
      <UserAvatar
        size="s40"
        user={{
          avatar: employee.access?.avatar,
          fullName: employeeName,
        }}
      />
      <Box justifyContent="flexStart" alignItems="flexStart" gap="s4">
        <Text.Normal weight="medium">{employeeName}</Text.Normal>
        <Text.Normal>{getErrorReason(error.error ?? '')}</Text.Normal>
      </Box>
    </Box>
  )
}

const getErrorReason = (error: string) => {
  switch (error) {
    case 'closed_policy_period':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.closed_policy_period'
      )
    case 'no_mapping':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_mapping'
      )
    case 'no_overtime':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_overtime'
      )
    case 'no_payroll_pool':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_payroll_pool'
      )
    case 'no_policy_period':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.no_policy_period'
      )
    case 'not_assigned_to_any_policy':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.not_assigned_to_any_policy'
      )
    case 'source_id_error':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.source_id_error'
      )
    case 'not_same_period':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.not_same_period'
      )
    case 'unauthorized_to_create_supplement':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.unauthorized_to_create_supplement'
      )
    case 'unauthorized_to_delete_supplement':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.unauthorized_to_delete_supplement'
      )
    case 'already_compensated':
      return i18n.t(
        'time_tracking.modals.time_tracking_collection.bulk_compensation_response.errors.already_compensated'
      )
    default:
      return error
  }
}

export default observer(CustomResponseModal)
